import { alignedQuestion } from '@nextiva/engagement-ui';
import { compose } from 'recompose';
import { formatValidationTypeList, debouncedReduxForm } from 'sharedParts';
import { Field } from 'redux-form';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

import StylingToCSSLabelText from 'styling/hocs/StylingToCSS/StylingToCSSLabelText';
import propTypeBlockStyling from 'styling/config/editorBlock/PropTypes/blockStyling';
import propTypeStyleLabel from 'styling/config/editorBlock/PropTypes/styleLabel';
import sanitizeInputNodeValue from 'utils/sanitizeInputNodeValue';

import styles from './TextQuestionForm.scss';
import withTakerValidator from '../hoc/withTakerValidator';

class TextQuestionForm extends Component {
  static propTypes = {
    block: PropTypes.shape({
      required: PropTypes.bool,
      maximumCharacters: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
      remaining: PropTypes.bool,
      labelEnabled: PropTypes.bool,
      label: PropTypes.string,
      formatValidationType: PropTypes.oneOf(
        Object.keys(formatValidationTypeList),
      ),
    }).isRequired,
    cancelSubmit: PropTypes.func.isRequired,
    flushSubmit: PropTypes.func.isRequired,
    inputType: PropTypes.string,
    readOnly: PropTypes.bool,
    FieldComponent: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func,
    styleLabel: propTypeStyleLabel,
    styling: propTypeBlockStyling,
    validator: PropTypes.arrayOf(PropTypes.func),
  };

  static isFieldInvalid = ({ meta: { submitFailed, invalid } }) =>
    submitFailed && invalid;

  static renderCharactersRemaining = n => `${n} characters remaining`;

  componentWillUnmount() {
    this.props.cancelSubmit();
  }

  renderInput = ({
    maximumCharacters,
    remaining,
    labelEnabled,
    label,
    FieldComponent,
    ...field
  }) => (
    <div className={styles.container}>
      {TextQuestionForm.isFieldInvalid(field) && (
        <div className={styles.errorMessage}>{field.meta.error}</div>
      )}
      <FieldComponent
        tipClassName={cx(styles.tip, styles.up)}
        label={labelEnabled ? label : undefined}
        {...field}
      />
      {remaining &&
        maximumCharacters && (
          <div className={styles.statusMessage}>
            {TextQuestionForm.renderCharactersRemaining(
              maximumCharacters - field.input.value.length,
            )}
          </div>
        )}
    </div>
  );

  handleFieldChange = e => {
    if (e.target.value.length > this.props.block.maximumCharacters) {
      e.preventDefault();
    }
  };

  render() {
    const {
      block: { maximumCharacters, remaining, labelEnabled = true, label },
      readOnly,
      FieldComponent,
      inputType,
      flushSubmit,
      validator,
      styleLabel,
      styling,
    } = this.props;

    const labelBody = <span style={styleLabel}>{label}</span>;

    return (
      <Field
        name="value"
        component={this.renderInput}
        onBlur={flushSubmit}
        onChange={this.handleFieldChange}
        normalize={sanitizeInputNodeValue}
        validate={validator}
        props={{
          FieldComponent,
          remaining,
          label: labelBody,
          readOnly,
          labelEnabled,
          maximumCharacters,
          type: inputType,
          labelAlign: 'out',
          className: styles.textInput,
          inputClassName: styles.textAreaInput,
          styling,
        }}
      />
    );
  }
}

export default compose(
  alignedQuestion,
  debouncedReduxForm({
    debounceOptions: { leading: false },
    form: 'TextQuestionForm',
    isAutoSubmit: true,
  }),
  withTakerValidator,
  StylingToCSSLabelText,
)(TextQuestionForm);
